import { ORDER_STATE_LABEL, type CaseStatusKey, type ShippingBadgeKey } from '@/domain';
import { formatDay } from '@/shared/format/instant';
import { strings } from '@/shared/strings';

/**
 * The words behind the shipping pill, and the one figure beside it. Section 7
 * of the request of 13 September 2026.
 *
 * Beside the components rather than inside them so a screen composing its own
 * status cell, as Orders does, says exactly the same words as the pill.
 */
export const SHIPPING_BADGE_LABEL: Record<ShippingBadgeKey, string> = {
  onHold: strings.shipping.onHold,
  pendingDispatch: strings.shipping.pendingDispatch,
  awaitingWaybill: strings.shipping.awaitingWaybill,
  shipped: strings.shipping.shipped,
  outForDelivery: strings.shipping.outForDeliveryBadge,
  received: strings.shipping.receivedBadge,
  notYet: strings.shipping.notShippedYet,
};

/**
 * How many lab days ago a case was collected.
 *
 * Whole calendar days in lab time, not hours: the desk asks "has this been
 * sitting since Monday", not "is it 31 hours old". Both instants go through
 * `formatDay`, so a case collected at two in the morning Dubai time counts from
 * that morning rather than from the previous UTC day.
 */
export function daysSince(instant: string, now: string): number {
  const from = Date.parse(`${formatDay(instant)}T00:00:00Z`);
  const to = Date.parse(`${formatDay(now)}T00:00:00Z`);
  return Math.max(0, Math.round((to - from) / 86_400_000));
}

/** "today", "yesterday", "4 days ago". */
export function collectedAgo(days: number): string {
  if (days === 0) return strings.shipping.collectedToday;
  if (days === 1) return strings.shipping.collectedYesterday;
  return `${days} ${strings.shipping.collectedDaysAgo}`;
}

/**
 * What each status a case can read as is called. Section 6 of the request of
 * 16 September 2026: the Status filter offered four order states while the
 * column beside it showed nine, so a reader could see a row saying Shipped and
 * had no way to ask for every row saying Shipped.
 *
 * Built from the two vocabularies that already existed rather than a third of
 * its own: the order states a case is read by before it leaves the lab, and the
 * shipping words it is read by afterwards.
 */
export const CASE_STATUS_LABEL: Record<CaseStatusKey, string> = {
  production: ORDER_STATE_LABEL.production.label,
  ready_deliver: ORDER_STATE_LABEL.ready_deliver.label,
  held: ORDER_STATE_LABEL.held.label,
  // "Pending dispatch" rather than "Paid": on a status column the reader is
  // asking where the case is, and paid-and-ready is waiting for a courier.
  paid: strings.shipping.pendingDispatch,
  awaitingWaybill: strings.shipping.awaitingWaybill,
  shipped: strings.shipping.shipped,
  outForDelivery: strings.shipping.outForDeliveryBadge,
  received: strings.shipping.receivedBadge,
};
